import { useMemo, useState } from "react";
import type { SystemTile } from "../types";
import { tileImageUrl } from "../lib/tiles";

function tileName(tile: SystemTile): string {
  return tile.planets.map((planet) => planet.name).join(" / ") || "Empty System";
}

type SystemReplacementPickerProps = {
  tiles: SystemTile[];
  onChoose: (tile: SystemTile) => void;
  onCancel: () => void;
  title?: string;
  eyebrow?: string;
  help?: string;
  emptyMessage?: string;
};

export function SystemReplacementPicker({
  tiles,
  onChoose,
  onCancel,
  title = "Select an unused system",
  eyebrow = "Choose system",
  help = "Choose from system tiles that are not currently in use.",
  emptyMessage = "No unused systems match that search.",
}: SystemReplacementPickerProps) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return tiles;
    return tiles.filter((tile) =>
      `${tile.id} ${tileName(tile)} ${tile.anomalies.join(" ")} ${tile.wormholes.join(" ")}`
        .toLowerCase()
        .includes(normalized),
    );
  }, [query, tiles]);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onCancel}>
      <section
        className="modal-card replacement-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="system-picker-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-heading">
          <div>
            <span className="eyebrow">{eyebrow}</span>
            <h2 id="system-picker-title">{title}</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onCancel}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <p className="modal-help">{help}</p>
        <input
          className="search-field"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by tile number, planet, anomaly, or wormhole"
          autoFocus
        />
        <div className="replacement-grid">
          {filtered.map((tile) => (
            <button
              type="button"
              className="replacement-option"
              key={tile.id}
              onClick={() => onChoose(tile)}
            >
              <img src={tileImageUrl(tile.id)} alt={`Tile ${tile.id}`} />
              <span>
                <strong>#{tile.id}</strong>
                {tileName(tile)}
              </span>
              <small>
                {tile.resources}/{tile.influence} · {tile.planetCount} planet
                {tile.planetCount === 1 ? "" : "s"}
              </small>
            </button>
          ))}
          {!filtered.length && <div className="empty-state">{emptyMessage}</div>}
        </div>
      </section>
    </div>
  );
}
